import React, { ReactNode } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import type { PermissionString } from '../permissions/catalog';
import { RouteSkeleton } from '../../../Components/Skeleton/RouteSkeleton';
import { PermissionDenied } from './PermissionDenied';
import './ProtectedRoute.css';

import { createLogger } from '../../../utils/logger';
const logger = createLogger('features-auth');

type RequiredPermission = PermissionString | string | readonly PermissionString[];

// Array.isArray does not narrow a readonly array out of a union, hence the guard.
function isPermissionList(
  permission: RequiredPermission,
): permission is readonly PermissionString[] {
  return Array.isArray(permission);
}

interface ProtectedRouteProps {
  /**
   * Atomic permission required to access this route (CASL). Checked with can().
   * Use the PERMISSIONS constants (e.g. PERMISSIONS.agent.kb.view).
   * A list means ANY one of them grants access; an empty list grants nothing.
   */
  requiredPermission?: RequiredPermission;

  /**
   * Role gating — for routes that are role-scoped rather than permission-scoped
   * (internal Silia tools, docs). Not everything maps to an atomic permission.
   */
  requiredRoles?: string[];

  /**
   * How to react when a check fails: `'redirect'` (default) sends the user to
   * `/unauthorized`, leaving whatever shell/layout they were in. `'inline'`
   * instead renders `PermissionDenied` in place — for routes that live inside
   * a section shell (sidebar, tabs) where navigating away is worse UX than
   * just showing the denial where the content would have been.
   */
  denyBehavior?: 'redirect' | 'inline';

  children?: ReactNode;
}

export function ProtectedRoute({
  requiredPermission,
  requiredRoles = [],
  denyBehavior = 'redirect',
  children,
}: ProtectedRouteProps) {
  const { isAuthenticated, user, loading, can, atomicPermissionsLoading } = useAuth();

  // Show loading while authenticating.
  if (loading) {
    return <RouteSkeleton />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  // Role-based gating (internal tools, docs — no atomic permission exists).
  if (requiredRoles.length > 0) {
    if (!user?.role || !requiredRoles.includes(user.role)) {
      return denyBehavior === 'inline' ? <PermissionDenied /> : <Navigate to="/unauthorized" />;
    }
    return children || <Outlet />;
  }

  // Atomic-permission gating (CASL). Wait for /me/permissions to resolve before
  // evaluating can() — otherwise the empty initial Set denies access during the
  // round-trip and wrongly redirects to /unauthorized on refresh/deep-link.
  if (requiredPermission) {
    if (atomicPermissionsLoading) {
      return <RouteSkeleton />;
    }
    const allowed = isPermissionList(requiredPermission)
      ? requiredPermission.some((permission) => can(permission))
      : can(requiredPermission);
    if (!allowed) {
      const missing = isPermissionList(requiredPermission)
        ? requiredPermission.join(' OR ')
        : requiredPermission;
      logger.warn(`Access denied — missing permission: "${missing}"`);
      return denyBehavior === 'inline' ? <PermissionDenied /> : <Navigate to="/unauthorized" />;
    }
    return children || <Outlet />;
  }

  // No restriction beyond authentication.
  return children || <Outlet />;
}
